import type { QuestionnaireTemplate } from '@/pages/sidebar/SettingsPage/types';

export interface QuestionnaireTemplateWithRef extends QuestionnaireTemplate {
  templateRef: string;
}

export const DEFAULT_QUESTIONNAIRE_TEMPLATES: QuestionnaireTemplateWithRef[] = [
  {
    id: 'qn-1',
    templateRef: 'templates/questionnaires/wedding-day-questionnaire.json',
    name: 'Wedding Day Questionnaire',
    fields: [
      {
        id: 'f1',
        type: 'textarea',
        label:
          "Please fill out the following form with locations and the Photographer's Start times.",
        required: true,
        placeholder:
          'Location should be actual addresses. Start times should be written like: 10:00AM or 3:00PM.',
      },
      {
        id: 'f2',
        type: 'textarea',
        label:
          'Please list yourself and 3 other phone numbers + names of people involved in the wedding that we could reach out to if we need something on the day of.',
        required: true,
        placeholder:
          'Please list yourself and 3 other phone numbers + names of people involved in the wedding.',
      },
      {
        id: 'f3',
        type: 'text',
        label: 'Photographer Start & End Time',
        required: true,
      },
      {
        id: 'f4',
        type: 'textarea',
        label:
          'Please list your names + phone numbers + any other important contact info we should have day of.',
        required: true,
        placeholder:
          'Please list your names + phone numbers + any other important contact info we should have day of.',
      },
      {
        id: 'f5',
        type: 'text',
        label: 'Getting Ready Spouse 1 Start Time',
        required: false,
        placeholder: 'Getting Ready Spouse 1 Start Time:',
      },
      {
        id: 'f6',
        type: 'text',
        label: 'Getting Ready Spouse 1 Address',
        required: false,
        placeholder: 'Getting Ready Spouse 1 Address:',
      },
      {
        id: 'f7',
        type: 'text',
        label: 'Getting Ready Spouse 2 Start Time',
        required: false,
        placeholder: 'Getting Ready Spouse 2 Start Time:',
      },
      {
        id: 'f8',
        type: 'text',
        label: 'Getting Ready Spouse 2 Address',
        required: false,
        placeholder: 'Getting Ready Spouse 2 Address:',
      },
      {
        id: 'f9',
        type: 'select',
        label: 'Are you doing a first look?',
        required: false,
        options: ['Yes', 'No'],
      },
      {
        id: 'f10',
        type: 'text',
        label: 'If yes, at what time and location?',
        required: false,
        placeholder: 'If yes, at what time and location?',
      },
      {
        id: 'f11',
        type: 'text',
        label: 'Ceremony Start Time',
        required: true,
        placeholder: 'Ceremony Start Time:',
      },
      {
        id: 'f12',
        type: 'text',
        label: 'Ceremony Address',
        required: true,
        placeholder: 'Ceremony Address:',
      },
      {
        id: 'f13',
        type: 'text',
        label: 'Ceremony Length',
        required: false,
        placeholder: 'Ceremony Length:',
      },
      {
        id: 'f14',
        type: 'text',
        label: 'Family Photo Start Time + Location',
        required: false,
        placeholder: 'Family Photo Start Time + Location:',
      },
      {
        id: 'f15',
        type: 'text',
        label: 'Couple + Wedding Party Photo Start Time + Location',
        required: false,
        placeholder: 'Couple + Wedding Party Photo Start Time + Location:',
      },
      {
        id: 'f16',
        type: 'text',
        label: 'Cocktail Hour Start Time',
        required: false,
        placeholder: 'Cocktail Hour Start Time:',
      },
      {
        id: 'f17',
        type: 'textarea',
        label: 'Did you want to be at your cocktail hour or doing portraits during that time?',
        required: false,
      },
      {
        id: 'f18',
        type: 'text',
        label: 'What time would you like to arrive at the reception location at?',
        required: false,
      },
      {
        id: 'f19',
        type: 'text',
        label: 'Dinner Start Time',
        required: false,
        placeholder: 'Dinner Start Time:',
      },
      {
        id: 'f20',
        type: 'text',
        label: 'What time are speeches at and how many?',
        required: false,
      },
      {
        id: 'f21',
        type: 'text',
        label: 'What time is the first dance? Are there other formal dances?',
        required: false,
      },
      {
        id: 'f22',
        type: 'text',
        label: 'Reception Address',
        required: true,
        placeholder: 'Reception Address:',
      },
      {
        id: 'f23',
        type: 'textarea',
        label:
          'In the event that things run late, what is the last event you would like the photographers to stay until the end of?',
        required: false,
      },
      {
        id: 'f24',
        type: 'textarea',
        label:
          'If you have a reception timeline or full production schedule, please link it below.',
        required: false,
        placeholder:
          'If you have a reception timeline, please e-mail that to me as well or link it below.',
      },
      {
        id: 'f25',
        type: 'textarea',
        label:
          'Do you have any Preferred Photo Locations? Or a preferred style of backdrop for photos?',
        required: false,
      },
      {
        id: 'f26',
        type: 'textarea',
        label: 'Any additional notes or things we should know?',
        required: false,
      },
      {
        id: 'f27',
        type: 'textarea',
        label: 'What are your wedding colors?',
        required: false,
        placeholder:
          'If you have bridesmaids, what color are their dresses? What color are your florals, etc.?',
      },
      {
        id: 'f28',
        type: 'text',
        label: 'How many people are in the wedding party/on each side?',
        required: false,
      },
      {
        id: 'f29',
        type: 'textarea',
        label: 'Do you have a limo or bus or are you driving yourselves?',
        required: false,
      },
    ],
  },
];

/**
 * Schema versioning for persisted questionnaire template overrides.
 *
 * When the canonical default questionnaire fields change shape (new field set,
 * renamed fields, restructured options), bump `QUESTIONNAIRE_TEMPLATES_SCHEMA_VERSION`.
 * On next load, `ensureQuestionnaireTemplatesSchema` clears any stale localStorage
 * overrides keyed under `drahft_questionnaire_templates` so the new defaults render.
 */
export const QUESTIONNAIRE_TEMPLATES_STORAGE_KEY = 'drahft_questionnaire_templates';
export const QUESTIONNAIRE_TEMPLATES_SCHEMA_VERSION = 2;
const QUESTIONNAIRE_TEMPLATES_SCHEMA_VERSION_KEY = 'drahft_questionnaire_templates_v';

export const ensureQuestionnaireTemplatesSchema = (): void => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  try {
    const stored = Number(localStorage.getItem(QUESTIONNAIRE_TEMPLATES_SCHEMA_VERSION_KEY) || 0);
    if (stored < QUESTIONNAIRE_TEMPLATES_SCHEMA_VERSION) {
      localStorage.removeItem(QUESTIONNAIRE_TEMPLATES_STORAGE_KEY);
      localStorage.setItem(
        QUESTIONNAIRE_TEMPLATES_SCHEMA_VERSION_KEY,
        String(QUESTIONNAIRE_TEMPLATES_SCHEMA_VERSION)
      );
    }
  } catch {
    // Storage may be unavailable (private mode, SSR) - safe to ignore.
  }
};
